import React from "react";
import styled from "styled-components";

const Container = styled.div<{
    fullWidth: boolean;
    hasPreview: boolean;
    margin?: string;
}>`
    cursor: pointer;
    position: relative;
    width: ${(p) => (p.fullWidth ? "100%" : "auto")};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    & input {
        display: none;
        border: 0;
        border-bottom: 1px solid #dcdbdc;
        width: ${(p) => (p.fullWidth ? "100%" : "auto")};
        outline: none;
        font-family: urdu;
        font-weight: 500;
        font-size: 18px;
        margin: ${(p) => p.margin || "40px 0 0 0"};

        &:focus {
            border-bottom-color: #000;
        }
    }

    & svg {
        margin-top: ${(p) => (p.hasPreview ? 15 : 40)}px;
    }
`;

const Icon = () => (
    <svg
        version="1.1"
        width="30"
        height="30"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M17.5 19h-16c-0.827 0-1.5-0.673-1.5-1.5v-2c0-0.276 0.224-0.5 0.5-0.5s0.5 0.224 0.5 0.5v2c0 0.276 0.224 0.5 0.5 0.5h16c0.276 0 0.5-0.224 0.5-0.5v-2c0-0.276 0.224-0.5 0.5-0.5s0.5 0.224 0.5 0.5v2c0 0.827-0.673 1.5-1.5 1.5z"
            fill="#000000"
        />
        <path
            d="M14.854 8.646l-5-5c-0.195-0.195-0.512-0.195-0.707 0l-5 5c-0.195 0.195-0.195 0.512 0 0.707s0.512 0.195 0.707 0l4.146-4.146v10.293c0 0.276 0.224 0.5 0.5 0.5s0.5-0.224 0.5-0.5v-10.293l4.146 4.146c0.098 0.098 0.226 0.146 0.354 0.146s0.256-0.049 0.354-0.146c0.195-0.195 0.195-0.512 0-0.707z"
            fill="#000000"
        />
    </svg>
);

const PreviewLink = styled.a`
    display: block;
    width: 100%;
    margin: 40px auto 0 auto;
    text-align: center;
`;
const PreviewImg = styled.img`
    max-width: 100%;
`;
function Preview({ src }: Record<"src", string>) {
    return (
        <PreviewLink href={src} target="_blank">
            <PreviewImg src={src} />
        </PreviewLink>
    );
}

const Placeholder = styled.div`
    pointer-events: none;
    user-select: none;
    margin-top: 15px;
    margin-bottom: 15px;
    color: #000;
    font-size: 16px;
`;

interface FileInputProps {
    placeholder: string;
    name: string;
    value: string;
    margin?: string;
    accept: string;
    readOnly: boolean;
    focusOnLoad: boolean;
    fullWidth: boolean;
    onChange: (name: string, file: File) => void;
}
interface FileInputState {
    preview: string | null;
}
export class FileInput extends React.Component<FileInputProps, FileInputState> {
    private field: React.RefObject<HTMLInputElement>;

    constructor(props: FileInputProps) {
        super(props);
        this.field = React.createRef<HTMLInputElement>();
        this.state = {
            preview: null,
        };
    }

    componentDidMount() {
        const c = this.field.current;
        if (c && this.props.readOnly && this.props.focusOnLoad) {
            c.focus();
        }
    }

    onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = evt.target;
        if (!files || files.length === 0) {
            return;
        }

        const file = files[0];
        if (!file) {
            return;
        }

        const { type, size } = file;
        if (["image/jpeg", "image/png"].includes(type)) {
            const kbSize = size / 1000;
            const maxSize = 1000.0;
            if (kbSize <= maxSize) {
                this.setState({ preview: URL.createObjectURL(file) });
                this.props.onChange(this.props.name, file);
            }
        }
    };

    placeholder() {
        const { placeholder } = this.props;
        return <Placeholder>{placeholder}</Placeholder>;
    }

    onClick = () => {
        if (this.field && this.field.current) {
            this.field.current.click();
        }
    };

    render() {
        const { preview } = this.state;
        const { margin, fullWidth, accept, value } = this.props;

        const hasPreview = !!value || !!preview;

        return (
            <Container
                onClick={this.onClick}
                fullWidth={fullWidth}
                hasPreview={hasPreview}
                margin={margin}
            >
                {hasPreview && (
                    <Preview src={preview !== null ? preview : value} />
                )}

                <Icon />
                {this.placeholder()}

                <input
                    ref={this.field}
                    type="file"
                    accept={accept}
                    onChange={this.onChange}
                />
            </Container>
        );
    }
}
